import { useEffect } from 'react';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { BaseError } from '../../helpers/BaseError';

import useRoleFlags from 'hooks/auth/useRoleFlags';

// models
import { FormulaCategory } from '../../models/formula.model';

// services
import API from '../../services/api';

// helpers
import { queryClient } from '../../AppComposer';
import messageContentQueryKeys from '../message-content/_queryKey';
import useGetCurrentMessageContent from '../message-content/useGetCurrentMessageContent';
import formulaKeys from './_queryKeys';

const updateMessageCount = async (
    formulaId: number,
): Promise<{ message_count: number }> => {
    const { data } = await API.updateFormulaMessageCount(formulaId);

    return data;
};

function useMessageCountUpdateQueryOriginal(
    formulaId: number,
    options?: UseQueryOptions<{ message_count: number }, BaseError>,
) {
    const { hasWriteAccess } = useRoleFlags();
    return useQuery<{ message_count: number }, BaseError>({
        queryKey: formulaKeys.formulaMessageCountUpdate(formulaId),
        queryFn: () => updateMessageCount(formulaId),

        enabled: !!formulaId && hasWriteAccess,
        ...(options || {}),
    });
}

export default function useMessageCountUpdateQuery(formulaId: number) {
    const query = useMessageCountUpdateQueryOriginal(formulaId);
    const { currentMessageContentId } = useGetCurrentMessageContent(formulaId);

    useEffect(() => {
        if (!query.data?.message_count) return;

        const run = async () => {
            const message_count = query.data.message_count;

            const previousFormula: any = await queryClient.getQueryData(
                formulaKeys.formulaById(formulaId),
            );

            if (previousFormula) {
                queryClient.setQueryData(
                    formulaKeys.formulaById(formulaId),
                    (data: any) => (data ? { ...data, message_count } : data),
                );
                if (currentMessageContentId) {
                    queryClient.setQueryData(
                        messageContentQueryKeys.messageContent(
                            currentMessageContentId,
                        ),
                        (data: any) =>
                            data ? { ...data, message_count } : data,
                    );
                }
                queryClient.invalidateQueries({
                    queryKey: formulaKeys.formulaById(formulaId),
                });
                if (currentMessageContentId) {
                    queryClient.invalidateQueries({
                        queryKey: messageContentQueryKeys.messageContent(
                            currentMessageContentId,
                        ),
                    });
                }

                Object.keys(FormulaCategory).forEach(async (category) => {
                    const catalogKey = formulaKeys.formulaCatalog(
                        category as FormulaCategory,
                        previousFormula.message_type,
                    );

                    const hasData = !!(await queryClient.getQueryData(
                        catalogKey,
                    ));

                    if (hasData) {
                        queryClient.setQueryData(catalogKey, (data: any) => {
                            if (!data) return data;
                            const formulas =
                                data[previousFormula?.status] ?? [];
                            return {
                                ...data,
                                [previousFormula?.status]: formulas.map(
                                    (item: any) =>
                                        item.id === formulaId
                                            ? { ...item, message_count }
                                            : item,
                                ),
                            };
                        });
                    }
                });
            }
        };

        run();
    }, [query.data?.message_count, formulaId, currentMessageContentId]);

    return query;
}
